import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Stack,
  Paper,
  Snackbar,
  Alert,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { LoadingButton } from "@mui/lab";
import axios from "axios";
import { BASE_API_URL } from "../utils/constants";

const UploadAbacus = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // success, error, warning, info

  const handleFileChange = (e) => {
    // Only set the first file if multiple are somehow selected
    const selectedFile = e.target.files ? e.target.files[0] : null;
    if (selectedFile) {
        setFile(selectedFile);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleUpload = async () => {
    if (!file) {
      showSnackbar("Please select a file first.", "warning");
      return;
    }

    const formData = new FormData();
    console.log("Attempting to upload file:", file.name);
    formData.append("file", file);
    setUploading(true);
    
    try {
      const response = await axios.post(`${BASE_API_URL}/bundleSkills/abacus`, formData, {
        headers: {
          Authorization: localStorage.getItem("token"),
        },
      });
      showSnackbar("Successfully uploaded Abacus data!", "success");
      setFile(null);
    } catch (err) {
      showSnackbar("Failed to upload Abacus data. Please try again.", "error");
      console.error("Unable to upload abacus data-bundleskills ", err);
    } finally {
      setUploading(false);
    }
  };

  const fileSelected = !!file;

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ minHeight: "80vh", p: 2 }}
    >
      <Paper
        elevation={6}
        sx={{
          p: 6,
          width: "100%",
          maxWidth: 550,
          textAlign: "center",
          borderRadius: 4,
          backgroundColor: '#ffffff',
        }}
      >
        <Typography variant="h4" gutterBottom fontWeight={700} sx={{color:"#7300bd"}}>
          Abacus Data Upload
        </Typography>

        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4 }}>
          Upload your file (**.xlsx** or **.csv** format).
        </Typography>

        {/* --- File Input Area --- */}
        <Box
          sx={{
            border: fileSelected ? "2px solid #4caf50" : "2px dashed #757575", // Green border if file selected
            borderRadius: 3,
            p: 5,
            textAlign: "center",
            mb: 4,
            bgcolor: fileSelected ? "#e8f5e9" : "#f5f5f5", // Light green background if selected
            transition: 'all 0.3s ease-in-out',
            "&:hover": { bgcolor: fileSelected ? "#e8f5e9" : "#ebebeb" },
          }}
        >
          {fileSelected ? (
            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 60, mb: 1 }} />
          ) : (
            <CloudUploadIcon color="action" sx={{ fontSize: 60, mb: 1 }} />
          )}
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {/* {fileSelected ? `Selected: ${file.name}` : "Max file size: 10MB"} */}
          </Typography>
          
          <Button variant="outlined" component="label" size="large" sx={{ marginBottom:"3px",
    color: "#7300bd",
    borderColor: "#7300bd",
    "&:hover": {
      borderColor: "#7300bd",
      backgroundColor: "rgba(128, 0, 128, 0.04)", // light purple tint on hover
    },
  }}>
            {fileSelected ? "Change File" : "Choose File"}
            {/* The actual hidden file input */}
            <input
              id="file-input"
              type="file"
              hidden
              accept=".xlsx,.csv"
              onChange={handleFileChange}
            />
          </Button>

          {fileSelected && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
            </Typography>
          )}
        </Box>
        
        {/* --- Upload Button (using LoadingButton) --- */}
        <LoadingButton
          loading={uploading}
          loadingIndicator="Uploading..."
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          onClick={handleUpload}
          disabled={!fileSelected} // Disable if no file is selected
          startIcon={<CloudUploadIcon />}
        >
          Upload Data
        </LoadingButton>
      </Paper>

      {/* --- Snackbar for Notifications --- */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity} 
          sx={{ width: '100%' }}
          variant="filled" 
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default UploadAbacus;